import { useParams, Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, categoryDetails } from '@/mocks/categories';
import { articles, type Article } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState(slug || categories[0].slug);
  const [filteredArticles, setFilteredArticles] = useState<Article[]>([]);

  useEffect(() => {
    if (slug && categoryDetails[slug]) {
      setActiveCategory(slug);
    }
  }, [slug]);

  useEffect(() => {
    setFilteredArticles(articles.filter((a) => a.category === activeCategory));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeCategory]);

  const currentCategory = categoryDetails[activeCategory] || categoryDetails[categories[0].slug];

  const handleCategoryChange = (newSlug: string) => {
    setActiveCategory(newSlug);
    navigate(`/category/${newSlug}`, { replace: true });
  };

  const collectionSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - 网页版免费91`,
    description: currentCategory.description,
    url: `https://avvg.cn/category/${activeCategory}`,
    hasPart: filteredArticles.map((a) => ({
      '@type': 'Article',
      headline: a.title,
      url: `https://avvg.cn/article/${a.slug}`,
    })),
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(collectionSchema)}</script>
      <script type="application/ld+json">{JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: `${currentCategory.name} - 网页版免费91`,
        description: currentCategory.description,
        url: `https://avvg.cn/category/${activeCategory}`,
      })}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          {/* Category Header */}
          <section className="bg-background-100 py-12 md:py-16 px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="mb-4">
                <Breadcrumb
                  items={[
                    { label: '首页', href: '/' },
                    { label: currentCategory.name },
                  ]}
                />
              </div>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 flex items-center justify-center bg-primary-100 rounded-lg">
                  <i className={`${currentCategory.icon} text-2xl text-primary-600`}></i>
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
                    {currentCategory.name}
                  </h1>
                  <p className="text-sm md:text-base text-foreground-600 mt-1">{currentCategory.description}</p>
                </div>
              </div>
            </div>
          </section>

          {/* Category Tabs & Content */}
          <section className="py-10 md:py-14 px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              {/* Tab Switcher */}
              <div className="flex flex-wrap items-center gap-2 mb-10">
                {categories.map((cat) => (
                  <button
                    key={cat.slug}
                    onClick={() => handleCategoryChange(cat.slug)}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                      activeCategory === cat.slug
                        ? 'bg-primary-500 text-white'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>

              {/* Article Grid */}
              {filteredArticles.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredArticles.map((article) => (
                    <Link
                      key={article.slug}
                      to={`/article/${article.slug}`}
                      className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                    >
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={article.cardImageUrl}
                          alt={article.title}
                          title={`${article.title} - 网页版免费91`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-5">
                        <h2 className="text-base font-semibold text-foreground-900 mb-2 line-clamp-2 font-heading group-hover:text-primary-600 transition-colors duration-200">
                          {article.title}
                        </h2>
                        <p className="text-sm text-foreground-600 line-clamp-2 mb-3">{article.excerpt}</p>
                        <div className="flex items-center justify-between text-xs text-foreground-400">
                          <div className="flex items-center gap-3">
                            <span>{article.publishDate}</span>
                            <span>{article.readTime}</span>
                          </div>
                          <span className="flex items-center gap-1">
                            <i className="ri-eye-line"></i> {article.viewCount.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-full">
                    <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500 text-sm">该分类暂无文章</p>
                </div>
              )}
            </div>
          </section>
        </main>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}